/**
 * On-screen controls for phones and tablets.
 *
 * Nothing here changes the rules: the layer only feeds the same Input the keyboard uses
 * (held keys, momentary keys, an analog move direction and an aim angle), so speed, noise
 * and every interaction stay identical to desktop play. Buttons are DOM elements sized in
 * CSS pixels so they stay finger-sized at any canvas scale; sticks float wherever the
 * thumb lands on the canvas.
 */
import { UI_W, UI_H } from '@/engine/canvas';
import type { Input } from '@/engine/input';
import type { Game } from './game';
import { ITEMS } from './items';
import { FRIENDS } from './roster';
import { clickMapFloor } from './map';

const STICK_RADIUS = 44;
const DEAD_ZONE = 10;
/** Below this deflection the stick sneaks; beyond it the character walks. */
const SNEAK_ZONE = 0.55;
const LONG_PRESS_MS = 420;

export type TouchMode = 'play' | 'nav' | 'safe' | 'none';

/** Which control group a game state needs. Exported so the headless check can assert it. */
export function touchModeFor(g: Pick<Game, 'phase' | 'overlay'>): TouchMode {
  if (g.phase === 'play') {
    if (g.overlay === 'none') return 'play';
    if (g.overlay === 'safe') return 'safe';
    if (g.overlay === 'note' || g.overlay === 'help') return 'none';
    return 'nav';
  }
  return g.phase === 'title' || g.phase === 'select' ? 'nav' : 'none';
}

// ---------------- HUD hit testing (UI-layer coordinates, 960x540) ----------------

export type HudHit = { kind: 'slot'; index: number } | { kind: 'friend'; id: string } | null;

/** The hotbar and the portrait row are drawn by drawHud; keep these rectangles in step with it. */
export function hudHitAt(g: Game, ux: number, uy: number): HudHit {
  const slots = g.slotCount(g.leader);
  const sw = 44, x0 = UI_W / 2 - (slots * sw) / 2, sy = UI_H - 60;
  if (uy >= sy - 4 && uy <= sy + 44) {
    const i = Math.floor((ux - x0) / sw);
    if (i >= 0 && i < slots && ux - (x0 + i * sw) <= 40) return { kind: 'slot', index: i };
  }
  if (uy >= 6 && uy <= 108) {
    let px = 12;
    for (const fr of FRIENDS) {
      const ch = g.chars.get(fr.id);
      if (!ch || ch.state === 'outside') continue;
      if (ux >= px && ux < px + 60) return { kind: 'friend', id: fr.id };
      px += 68;
    }
  }
  return null;
}

/** A tap on a menu or overlay. Rows mirror the layouts in screens.ts; a second tap on the highlighted row confirms. */
export function handleTap(g: Game, ux: number, uy: number): void {
  const inp = g.input;
  const rowAt = (y0: number, step: number, count: number, x0 = 0, x1 = UI_W): number => {
    if (ux < x0 || ux >= x1) return -1;
    const i = Math.floor((uy - (y0 - 6)) / step);
    return i >= 0 && i < count ? i : -1;
  };
  const pick = (current: number, i: number, set: (i: number) => void) => {
    if (i < 0) return;
    if (i === current) inp.tapKey('Enter');
    else { set(i); g.audio.ui(); }
  };
  if (g.phase === 'title') {
    pick(g.menuIndex, rowAt(380, 32, g.hasSave ? 3 : 2), (i) => { g.menuIndex = i; });
    return;
  }
  if (g.phase === 'select') {
    const n = g.selectable().length;
    if (!n) return;
    const x0 = UI_W / 2 - (n * 100) / 2;
    const i = uy >= 150 && uy <= 270 ? Math.floor((ux - x0) / 100) : -1;
    if (i >= 0 && i < n) pick(g.selectIndex % n, i, (i) => { g.selectIndex = i; });
    return;
  }
  if (g.phase !== 'play') return;
  switch (g.overlay) {
    case 'pause':
      pick(g.menuIndex, rowAt(230, 34, 3), (i) => { g.menuIndex = i; });
      return;
    case 'note':
    case 'help':
      inp.tapKey('Enter');
      return;
    case 'map':
      clickMapFloor(g);
      return;
    case 'journal': {
      const entries = g.journal.length;
      if (!entries) return;
      const selected = Math.min(g.journalIndex, entries - 1);
      const start = Math.max(0, Math.min(selected - 4, entries - 11));
      const row = rowAt(106, 28, Math.min(entries, start + 11) - start, 48, 294);
      if (row >= 0 && start + row !== g.journalIndex) { g.journalIndex = start + row; g.audio.ui(); }
      return;
    }
    case 'bag': {
      const bag = g.bagTarget;
      if (!bag) return;
      const w = 300, gap = 40, x0 = UI_W / 2 - w - gap / 2, x1 = UI_W / 2 + gap / 2, y = 106;
      const side = ux >= x0 && ux < x0 + w ? 0 : ux >= x1 && ux < x1 + w ? 1 : -1;
      if (side < 0) return;
      const items = side === 0 ? bag.contents ?? [] : g.leader.inventory;
      const first = g.bagSide === side ? Math.max(0, g.bagIndex - 7) : 0;
      const row = rowAt(y + 44, 34, Math.min(8, items.length - first), side === 0 ? x0 : x1, (side === 0 ? x0 : x1) + w);
      if (row < 0) return;
      const index = first + row;
      if (g.bagSide === side && g.bagIndex === index) inp.tapKey('Enter');
      else { g.bagSide = side; g.bagIndex = index; g.audio.ui(); }
      return;
    }
  }
}

// ---------------- the DOM layer ----------------

interface Stick {
  id: number;
  role: 'move' | 'aim';
  ox: number;
  oy: number;
  base: HTMLElement;
  knob: HTMLElement;
}

export class TouchControls {
  root: HTMLElement;
  private input: Input;
  private sticks = new Map<number, Stick>();
  private runLatch = false;
  private sneaking = false;
  private mode: TouchMode | null = null;
  private labels = new Map<HTMLElement, string>();
  private portrait = false;

  constructor(private g: Game, private canvas: HTMLCanvasElement, private toInternal: (cx: number, cy: number) => [number, number], parent: HTMLElement = document.body) {
    this.input = g.input;
    this.input.touch = true;
    this.root = el('div', 'touch', parent);
    this.buildPlay();
    this.buildNav();
    this.buildKeypad();
    this.buildRotate();
    this.bindSticks();
    document.addEventListener('fullscreenchange', () => { this.syncFullscreen(); this.layout(); });
    this.syncFullscreen();
    window.addEventListener('blur', () => this.clearSticks());
    window.addEventListener('resize', () => this.layout());
    this.layout();
  }

  // ---- building
  private button(group: HTMLElement, id: string, label: string, code: string | (() => void), release?: () => void): HTMLElement {
    const b = el('button', `tb ${id}`, group);
    b.textContent = label;
    b.setAttribute('type', 'button');
    b.addEventListener('contextmenu', (e) => e.preventDefault());
    b.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      try { b.setPointerCapture(e.pointerId); } catch { /* synthetic pointer */ }
      b.classList.add('down');
      this.gesture();
      if (typeof code === 'string') this.input.press(code);
      else code();
    });
    const up = (e: PointerEvent) => {
      if (!b.classList.contains('down')) return;
      b.classList.remove('down');
      if (typeof code === 'string') this.input.release(code);
      else release?.();
      void e;
    };
    b.addEventListener('pointerup', up);
    b.addEventListener('pointercancel', up);
    b.addEventListener('lostpointercapture', up);
    return b;
  }
  private interact!: HTMLElement;
  private use!: HTMLElement;
  private flash!: HTMLElement;
  private run!: HTMLElement;
  private drop!: HTMLElement;
  private dropTimer = 0;

  private buildPlay(): void {
    const p = el('div', 'group play', this.root);
    this.interact = this.button(p, 'interact', '상호작용', 'KeyE');
    this.use = this.button(p, 'use', '사용', 'Space');
    this.flash = this.button(p, 'flash', '손전등', 'KeyF');
    this.run = this.button(p, 'run', '달리기', () => this.setRun(!this.runLatch));
    this.drop = this.button(p, 'drop', '놓기', () => {
      this.dropTimer = window.setTimeout(() => {
        this.dropTimer = 0;
        this.drop.classList.add('careful');
        // Hold C across the frame that reads G so the drop counts as careful.
        this.input.press('KeyC');
        this.input.press('KeyG');
        requestAnimationFrame(() => requestAnimationFrame(() => { this.input.release('KeyG'); this.input.release('KeyC'); this.drop.classList.remove('careful'); }));
      }, LONG_PRESS_MS);
    }, () => {
      if (!this.dropTimer) return;
      clearTimeout(this.dropTimer);
      this.dropTimer = 0;
      this.input.tapKey('KeyG');
    });
    const sig = el('div', 'signals', p);
    this.button(sig, 'sig', '따라와', 'Digit1');
    this.button(sig, 'sig', '대기', 'Digit2');
    this.button(sig, 'sig', '숨어', 'Digit3');
    const sys = el('div', 'system', p);
    this.fullscreenButtons.push(this.button(sys, 'sys full', '⛶', () => this.toggleFullscreen()));
    this.button(sys, 'sys', '❚❚', 'Escape');
    this.button(sys, 'sys', '지도', 'KeyM');
    this.button(sys, 'sys', '수첩', 'KeyJ');
  }
  /** Portrait: the game waits, but one tap can take the browser fullscreen and lock it to landscape. */
  private rotateHint!: HTMLElement;
  private buildRotate(): void {
    const r = el('div', 'rotate', this.root);
    el('div', 'rotate-title', r).textContent = '기기를 가로로 돌려 주세요';
    const b = el('button', 'tb rotate-full', r);
    b.setAttribute('type', 'button');
    b.textContent = '전체 화면으로 가로 플레이';
    b.addEventListener('pointerdown', (e) => { e.preventDefault(); this.input.onGesture?.(); this.enterFullscreen(); });
    b.addEventListener('click', (e) => { e.preventDefault(); this.enterFullscreen(); });
    this.rotateHint = el('div', 'rotate-hint', r);
    this.fullscreenButtons.push(b);
  }
  private buildNav(): void {
    const n = el('div', 'group nav', this.root);
    const pad = el('div', 'dpad', n);
    this.button(pad, 'up', '▲', 'ArrowUp');
    this.button(pad, 'left', '◀', 'ArrowLeft');
    this.button(pad, 'dn', '▼', 'ArrowDown');
    this.button(pad, 'right', '▶', 'ArrowRight');
    this.fullscreenButtons.push(this.button(n, 'full corner', '⛶', () => this.toggleFullscreen()));
    const ok = el('div', 'confirm', n);
    this.button(ok, 'cancel', '닫기', 'Escape');
    this.button(ok, 'ok', '확인', 'Enter');
  }
  private buildKeypad(): void {
    const k = el('div', 'group keypad', this.root);
    for (const d of ['1', '2', '3', '4', '5', '6', '7', '8', '9']) this.button(k, 'digit', d, () => this.input.typeText(d));
    this.button(k, 'digit back', '⌫', 'Backspace');
    this.button(k, 'digit', '0', () => this.input.typeText('0'));
    this.button(k, 'digit ok', '확인', 'Enter');
    this.button(k, 'digit cancel', '취소', 'Escape');
  }

  /** Runs inside every real touch: unlocks audio and, on the first one, asks for a fullscreen landscape view. */
  private fullscreenTried = false;
  private fullscreenButtons: HTMLElement[] = [];
  private gesture(): void {
    this.input.onGesture?.();
    if (this.fullscreenTried) return;
    this.fullscreenTried = true;
    if (this.fullscreenAvailable() && !document.fullscreenElement) this.enterFullscreen();
  }
  /** iPhone Safari has no fullscreen API and an installed web app is already fullscreen; hide the button there. */
  private fullscreenAvailable(): boolean {
    const standalone = typeof matchMedia === 'function' && (matchMedia('(display-mode: standalone)').matches || matchMedia('(display-mode: fullscreen)').matches);
    return !standalone && !!document.fullscreenEnabled && !!document.documentElement.requestFullscreen;
  }
  private enterFullscreen(): void {
    // Android Chrome hides its address bar and pins landscape; browsers only allow this inside a real gesture.
    void (async () => {
      try { await document.documentElement.requestFullscreen({ navigationUI: 'hide' }); } catch { /* not allowed */ }
      try { await (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.('landscape'); } catch { /* unsupported */ }
    })();
  }
  /** The ⛶ button: leaves fullscreen, or brings it back after the system UI dismissed it. */
  private toggleFullscreen(): void {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => { /* already gone */ });
    else this.enterFullscreen();
  }
  private syncFullscreen(): void {
    const on = !!document.fullscreenElement, show = this.fullscreenAvailable();
    for (const b of this.fullscreenButtons) { b.hidden = !show; b.classList.toggle('on', on); b.title = on ? '전체 화면 끄기' : '전체 화면'; }
    this.rotateHint.textContent = show
      ? '버튼을 누르면 브라우저가 전체 화면으로 바뀌고 가로로 고정됩니다.'
      : '이 브라우저는 페이지 전체 화면을 지원하지 않습니다. 공유 → 홈 화면에 추가로 실행하면 가로 전체 화면이 됩니다.';
  }

  // ---- sticks on the canvas
  private bindSticks(): void {
    const c = this.canvas;
    // Cancelling touchstart stops browsers from synthesising mouse events, which would
    // otherwise register a tap as a click that throws the selected item.
    c.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });
    c.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'touch') return;
      e.preventDefault();
      this.gesture();
      this.input.mouseKnown = false;
      if (touchModeFor(this.g) !== 'play') { this.input.tap(e.clientX, e.clientY); return; }
      const [ix, iy] = this.toInternal(e.clientX, e.clientY);
      const hit = hudHitAt(this.g, ix * 2, iy * 2);
      if (hit?.kind === 'slot') { if (hit.index !== this.g.selectedSlot) { this.g.selectedSlot = hit.index; this.g.audio.ui(); } return; }
      if (hit?.kind === 'friend') { this.g.takeControl(hit.id); return; }
      const role: Stick['role'] = e.clientX < window.innerWidth / 2 ? 'move' : 'aim';
      if ([...this.sticks.values()].some((s) => s.role === role)) return;
      try { c.setPointerCapture(e.pointerId); } catch { /* synthetic pointer */ }
      const base = el('div', `stick ${role}`, this.root), knob = el('div', 'knob', base);
      const s: Stick = { id: e.pointerId, role, ox: e.clientX, oy: e.clientY, base, knob };
      this.sticks.set(e.pointerId, s);
      this.placeStick(s, e.clientX, e.clientY);
    });
    c.addEventListener('pointermove', (e) => {
      const s = this.sticks.get(e.pointerId);
      if (s) this.moveStick(s, e.clientX, e.clientY);
    });
    const end = (e: PointerEvent) => {
      const s = this.sticks.get(e.pointerId);
      if (s) this.endStick(s);
    };
    c.addEventListener('pointerup', end);
    c.addEventListener('pointercancel', end);
  }
  private placeStick(s: Stick, x: number, y: number): void {
    s.base.style.left = `${s.ox}px`;
    s.base.style.top = `${s.oy}px`;
    s.knob.style.transform = `translate(${x - s.ox}px, ${y - s.oy}px)`;
  }
  private moveStick(s: Stick, x: number, y: number): void {
    let dx = x - s.ox, dy = y - s.oy;
    const d = Math.hypot(dx, dy);
    // The base follows the thumb once it leaves the ring, so direction changes stay cheap.
    if (d > STICK_RADIUS) { s.ox = x - (dx / d) * STICK_RADIUS; s.oy = y - (dy / d) * STICK_RADIUS; dx = x - s.ox; dy = y - s.oy; }
    this.placeStick(s, x, y);
    const m = Math.min(1, d / STICK_RADIUS);
    if (s.role === 'move') {
      if (d < DEAD_ZONE) { this.input.moveX = 0; this.input.moveY = 0; this.setSneak(false); return; }
      this.input.moveX = dx / d; this.input.moveY = dy / d;
      this.setSneak(m < SNEAK_ZONE);
    } else {
      this.input.aimAngle = d < DEAD_ZONE ? this.input.aimAngle : Math.atan2(dy, dx);
    }
  }
  private endStick(s: Stick): void {
    this.sticks.delete(s.id);
    s.base.remove();
    if (s.role === 'move') { this.input.moveX = 0; this.input.moveY = 0; this.setSneak(false); this.setRun(false); }
    else this.input.aimAngle = null;
  }
  private clearSticks(): void {
    for (const s of [...this.sticks.values()]) this.endStick(s);
  }
  private setSneak(on: boolean): void {
    if (on === this.sneaking) return;
    this.sneaking = on;
    if (on) this.input.press('KeyC'); else this.input.release('KeyC');
  }
  private setRun(on: boolean): void {
    if (on === this.runLatch) return;
    this.runLatch = on;
    this.run.classList.toggle('on', on);
    if (on) this.input.press('ShiftLeft'); else this.input.release('ShiftLeft');
  }

  // ---- per-frame sync with game state
  private layout(): void {
    const portrait = window.innerHeight > window.innerWidth;
    if (portrait !== this.portrait) { this.portrait = portrait; this.root.classList.toggle('portrait', portrait); }
    if (portrait && this.g.phase === 'play' && this.g.overlay === 'none') { this.g.overlay = 'pause'; this.g.menuIndex = 0; this.clearSticks(); }
  }
  update(): void {
    const g = this.g;
    const mode = touchModeFor(g);
    if (mode !== this.mode) {
      this.mode = mode;
      this.root.dataset.mode = mode;
      if (mode !== 'play') { this.clearSticks(); this.setRun(false); }
    }
    if (mode !== 'play') return;
    const c = g.leader;
    this.setLabel(this.interact, g.interaction?.label ?? '상호작용');
    this.interact.classList.toggle('idle', !g.interaction);
    const sel = c.inventory[g.selectedSlot];
    const verb = !sel ? '사용' : ITEMS[sel.kind].use === 'throw' ? '던지기' : ITEMS[sel.kind].use === 'flash' ? '플래시' : ITEMS[sel.kind].use === 'battery' ? '교체' : ITEMS[sel.kind].use === 'read' ? '읽기' : '사용';
    this.setLabel(this.use, verb);
    this.use.classList.toggle('idle', !sel);
    this.flash.classList.toggle('on', c.flashOn);
    this.setLabel(this.drop, this.sneaking ? '조심히' : '놓기');
  }
  private setLabel(b: HTMLElement, s: string): void {
    if (this.labels.get(b) === s) return;
    this.labels.set(b, s);
    b.textContent = s;
  }
}

/** Phones and tablets get the layer immediately; a desktop gets it the first time a finger touches the page. */
export function mountTouchControls(g: Game, canvas: HTMLCanvasElement, toInternal: (cx: number, cy: number) => [number, number]): { current: TouchControls | null } {
  const holder: { current: TouchControls | null } = { current: null };
  const mount = () => {
    if (!holder.current) holder.current = new TouchControls(g, canvas, toInternal);
  };
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  if (coarse || new URLSearchParams(location.search).has('touch')) mount();
  else window.addEventListener('pointerdown', (e) => { if (e.pointerType === 'touch') mount(); }, { passive: true });
  return holder;
}

function el(tag: string, className: string, parent: HTMLElement): HTMLElement {
  const e = document.createElement(tag);
  e.className = className;
  parent.appendChild(e);
  return e;
}
